import { Header } from '@components/Header';
import AddIcon from '@components/icons/Add.svg';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { formatPrice } from '@helpers/formatPrice';
import { YearEndSpecial } from '@pages/User/DashboardPage/components/YearEndSpecial';
import { useAuthContext } from '@providers/AuthProvider';
import { usePermission } from '@providers/PermissionProvider/usePermission';
import { routes } from '@routes';
import { EPermission } from 'app/types/enums/EPermission';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { DailyActivity } from './Daily';
import { DeadlineCalendar } from './DeadlineCalendar';

import { useDashboard } from './hooks/useDashboard';
import { PerformanceReview } from './Performance';

// const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// const getPrevMonthName = () => {
//   const now = new Date();
//   const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 15).getMonth();
//   return months[prevMonth];
// };

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { organization, workspace, me } = useAuthContext();

  const { dashboardInfo, isLoading } = useDashboard();
  const [, canEditRepositories] = usePermission(EPermission.editRepositories);

  if (!me.organizations.length) {
    navigate(routes.accountPage.make());
    return null;
  }

  const handleAddJob = () => {
    navigate(routes.newJob.make(organization?.slug ?? '', workspace?.slug ?? ''));
  };

  const handleAddCase = () => {
    navigate(
      {
        pathname: routes.newRepository.make(organization?.slug ?? ''),
      },
      {
        state: {
          from: routes.dashboard.make(organization?.slug ?? ''),
        },
      },
    );
  };

  const canSubmitJobs = Boolean(organization?.permissions?.submitJobs);
  const primaryAction = canSubmitJobs
    ? {
        key: 'create-job',
        label: 'Create New Job',
        icon: AddIcon,
        onClick: handleAddJob,
        testId: 'create_job_header_button',
      }
    : undefined;
  const secondaryActions =
    canSubmitJobs && canEditRepositories
      ? [
          {
            key: 'create-case',
            label: 'Create New Case',
            icon: AddIcon,
            onClick: handleAddCase,
            testId: 'header_case_button',
          },
        ]
      : [];

  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Dashboard - AutoScript</title>
        </Helmet>
        <Header title="Dashboard" primaryAction={primaryAction} actions={secondaryActions} />

        <LinearProgressLoader active={isLoading} />
        <div className="px-4 md:px-[30px] py-6 min-w-0 min-h-[calc(100vh-120px)]" />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard - AutoScript</title>
      </Helmet>
      <Header title="Dashboard" globalSearch={true} primaryAction={primaryAction} actions={secondaryActions} />

      <div className="px-4 md:px-[30px] py-6 min-w-0 min-h-[calc(100vh-120px)]">
        <YearEndSpecial />
        <div className="flex flex-col xl:flex-row gap-2.5 justify-between items-start">
          <div className="flex flex-wrap gap-2.5 justify-between items-start min-w-0 w-full">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 w-full">
              <div className="min-w-0 w-full min-h-[170px] rounded-[10px] p-5 relative bg-surface shadow-[0_8px_10px_var(--color-shadow)]">
                <div className="text-foreground-muted font-medium text-lg leading-[150%] whitespace-nowrap overflow-hidden text-ellipsis">
                  Organization Members
                </div>
                <div className="text-accent font-semibold text-[36px] leading-[47px] -mt-0.5">
                  {dashboardInfo?.members?.count} Total
                </div>
                <div className="text-foreground-muted font-medium text-lg leading-[150%]">Active last 7 days</div>
                <div className="text-foreground-muted font-medium text-lg leading-[150%]">
                  {dashboardInfo?.members?.active7days}{' '}
                  {dashboardInfo?.members?.active7days === 1 ? 'member' : 'members'}
                </div>
              </div>
              <div className="min-w-0 w-full min-h-[170px] rounded-[10px] p-5 relative bg-surface shadow-[0_8px_10px_var(--color-shadow)]">
                <div className="text-foreground-muted font-medium text-lg leading-[150%] whitespace-nowrap overflow-hidden text-ellipsis">
                  Workspaces Activity
                </div>
                <div className="text-accent font-semibold text-[36px] leading-[47px] -mt-0.5">
                  {dashboardInfo?.workspaceActivity?.count} Total
                </div>
                <div className="flex">
                  <div className="w-[calc(50%-5px)] border-r border-[color:var(--color-text-muted)] mr-2.5 pr-2.5">
                    <div className="text-foreground-muted font-medium text-lg leading-[150%] whitespace-nowrap overflow-hidden text-ellipsis">
                      ASR Rate
                    </div>
                    <div className="text-foreground-muted font-medium text-lg leading-[150%] whitespace-nowrap overflow-hidden text-ellipsis">
                      {formatPrice(dashboardInfo?.workspaceActivity?.tierRate ?? 0)}/Hr
                    </div>
                  </div>
                  <div className="w-[calc(50%-5px)]">
                    <div className="text-foreground-muted font-medium text-lg leading-[150%] whitespace-nowrap overflow-hidden text-ellipsis">
                      Billing
                    </div>
                    <div className="text-foreground-muted font-medium text-lg leading-[150%] whitespace-nowrap overflow-hidden text-ellipsis">
                      {dashboardInfo?.workspaceActivity?.billingCard}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="min-w-0 w-full flex flex-wrap gap-2.5 justify-between">
              <div className="w-full rounded-[10px] p-5 relative bg-surface shadow-[0_8px_10px_var(--color-shadow)]">
                <div className="text-title font-semibold text-base leading-[100%] whitespace-nowrap overflow-hidden text-ellipsis">
                  Performance Review
                </div>
                <PerformanceReview data={dashboardInfo?.performanceReview ?? {}} />
              </div>
              <div className="w-full rounded-[10px] p-5 relative bg-surface shadow-[0_8px_10px_var(--color-shadow)]">
                <div className="text-title font-semibold text-base leading-[100%] whitespace-nowrap overflow-hidden text-ellipsis">
                  Daily Active Members
                </div>
                <DailyActivity data={dashboardInfo?.dailyActivity ?? {}} />
              </div>
            </div>
          </div>
          <div className="min-w-0 w-full xl:w-[280px] xl:shrink-0 h-auto max-h-[670px] rounded-[10px] p-5 relative bg-surface shadow-[0_8px_10px_var(--color-shadow)]">
            <div className="text-title font-semibold text-base leading-[100%] whitespace-nowrap overflow-hidden text-ellipsis">
              Deadline Calendar
            </div>
            <DeadlineCalendar calendar={dashboardInfo?.deadlineCalendar ?? []} />
          </div>
        </div>
      </div>
    </>
  );
};
